import { useEffect, useRef, useCallback } from "react";

/**
 * Hook to prevent accidental navigation or page reloads.
 * Uses the browser's native `beforeunload` event to warn users
 * that they might lose unsaved changes or active sessions.
 * 
 * Note: Modern browsers ignore custom returnValue text for security reasons 
 * and display a generic "Changes you made may not be saved" dialog.
 * 
 * @param shouldPrevent - boolean indicating if the warning should be active
 */
export function usePreventUnload(shouldPrevent: boolean = true) {
    const isEnabled = useRef(shouldPrevent);

    // Sync ref when prop changes
    useEffect(() => {
        isEnabled.current = shouldPrevent;
    }, [shouldPrevent]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isEnabled.current) return;

            // Cancel the event as stated by the standard.
            event.preventDefault();
            // Chrome requires returnValue to be set.
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    // Provide a callback to manually turn it off right before calling window.location.href
    const disableUnloadWarning = useCallback(() => {
        isEnabled.current = false;
    }, []);

    return { disableUnloadWarning };
}
